import { CHECKR_BFF_API_BASE } from '../constants';
import { isSessionStorageSupported } from 'lib/helpers';

export const CAPABILITY_HEADER = 'X-Verification-Capability';
export const CSRF_HEADER = 'X-CSRF-Token';
const STORAGE_KEY_PREFIX = 'cp_verification_session:';
const ACTOR_KEY_PREFIX = 'cp_verification_actor:';
const CUSTOMER_ACTOR = 'customer';

interface VerificationSession {
  verificationId: string;
  csrfToken: string;
}

let session: VerificationSession | null = null;

const sessionPath = (verificationId: string) =>
  `${CHECKR_BFF_API_BASE}/verifications/${verificationId}/session`;

const markStored = (verificationId: string, stored: boolean) => {
  if (!isSessionStorageSupported()) return;
  const key = `${STORAGE_KEY_PREFIX}${verificationId}`;
  if (stored) {
    globalThis.sessionStorage.setItem(key, '1');
  } else {
    globalThis.sessionStorage.removeItem(key);
  }
};

const markActor = (verificationId: string, actorType: unknown) => {
  if (!isSessionStorageSupported() || typeof actorType !== 'string') return;
  globalThis.sessionStorage.setItem(
    `${ACTOR_KEY_PREFIX}${verificationId}`,
    actorType,
  );
};

const forgetActor = (verificationId: string) => {
  if (!isSessionStorageSupported()) return;
  globalThis.sessionStorage.removeItem(`${ACTOR_KEY_PREFIX}${verificationId}`);
};

// Outlives the session itself, so an expired customer link can be told apart
// from a candidate who needs to log in.
export const isCustomerVerificationSession = (verificationId?: string) =>
  !!verificationId &&
  isSessionStorageSupported() &&
  globalThis.sessionStorage.getItem(`${ACTOR_KEY_PREFIX}${verificationId}`) ===
    CUSTOMER_ACTOR;

export const hasStoredSession = (verificationId: string) =>
  isSessionStorageSupported() &&
  globalThis.sessionStorage.getItem(
    `${STORAGE_KEY_PREFIX}${verificationId}`,
  ) === '1';

// Resolves to the BFF's status, or 0 when it could not be reached.
const startSession = async (
  verificationId: string,
  init: RequestInit,
): Promise<number> => {
  try {
    const response = await fetch(sessionPath(verificationId), {
      ...init,
      credentials: 'include',
    });
    if (!response.ok) {
      markStored(verificationId, false);
      return response.status;
    }
    const { csrf_token: csrfToken, actor_type: actorType } =
      await response.json();
    session = { verificationId, csrfToken };
    markStored(verificationId, true);
    markActor(verificationId, actorType);
    return response.status;
  } catch {
    return 0;
  }
};

const isOk = (status: number) => status >= 200 && status < 300;

// A 404 means the exchange is off for the account, so the page must behave
// as if the link had no capability.
export const exchangeVerificationCapability = async (
  verificationId: string,
  capability: string,
) => {
  markActor(verificationId, CUSTOMER_ACTOR);
  const status = await startSession(verificationId, {
    method: 'POST',
    headers: { Accept: 'application/json', [CAPABILITY_HEADER]: capability },
  });
  if (status === 404) forgetActor(verificationId);
  return isOk(status);
};

export const restoreVerificationSession = async (verificationId: string) =>
  hasStoredSession(verificationId)
    ? isOk(
        await startSession(verificationId, {
          headers: { Accept: 'application/json' },
        }),
      )
    : false;

export const getVerificationSession = () => session;

export const hasVerificationSession = (verificationId?: string) =>
  !!verificationId && session?.verificationId === verificationId;

export const clearVerificationSession = () => {
  session = null;
};

export const withVerificationSession = <
  T extends { method?: string; headers?: Record<string, string> },
>(
  verificationId: string,
  options: T = {} as T,
) => {
  if (!session || !hasVerificationSession(verificationId)) return options;

  const write = !!options.method && options.method.toUpperCase() !== 'GET';
  return {
    ...options,
    credentials: 'include' as const,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
      ...(write ? { [CSRF_HEADER]: session.csrfToken } : {}),
    },
  };
};
